import { Icon } from "Components";
import { useEffect, useState } from "react";
import {
    Input,
    Select,
    Row,
    Col,
    Pagination,
} from "antd";
import TemplateCard from "./TemplateCard";
import { DEFAULT_TEMPLATES_PAGESIZE } from "../PhishingManager.constants";
import { CampaignThresholdData } from "../PhishingManager";

type Step1Props = {
    selectedTemplate: string | number | null;
    onSelect: (value: string | number | null) => void;
    onNext: () => void;
    onClose: () => void;
    thresholdData: null | CampaignThresholdData;
};

const TEMPLATES = [
    {
        value: 1,
        label: "Fake Login Page",
        category: "AI-Driven",
        difficulty: "Beginner",
    },
    {
        value: 2,
        label: "Booking Hotel Scam",
        category: "Hospitality-Specific",
        difficulty: "Advanced",
    },
    {
        value: 3,
        label: "Urgent CEO Request",
        category: "Core Essentials",
        difficulty: "Beginner",
    },
    {
        value: 4,
        label: "Urgent CEO Request",
        category: "AI-Driven",
        difficulty: "Beginner",
    },
    {
        value: 5,
        label: "Fake Login Page",
        category: "AI-Driven",
        difficulty: "Intermediate",
    },
    {
        value: 6,
        label: "Booking Hotel Scam",
        category: "Core Essentials",
        difficulty: "Beginner",
    },
    {
        value: 7,
        label: "Urgent CEO Request",
        category: "Hospitality-Specific",
        difficulty: "Advanced",
    },
    {
        value: 8,
        label: "Fake Login Page",
        category: "AI-Driven",
        difficulty: "Intermediate",
    },
    {
        value: 9,
        label: "Booking Hotel Scam",
        category: "Core Essentials",
        difficulty: "Beginner",
    },
    {
        value: 10,
        label: "Urgent CEO Request",
        category: "Hospitality-Specific",
        difficulty: "Advanced",
    },
];

export default function Step1({ selectedTemplate, onSelect, onNext, thresholdData }: Step1Props) {
    const [search, setSearch] = useState<string>("");
    const [language, setLanguage] = useState<string>("");
    const [category, setCategory] = useState<string>("");
    const [difficulty, setDifficulty] = useState<string>("");
    const [templatePage, setTemplatePage] = useState(1);
    const pageSize = DEFAULT_TEMPLATES_PAGESIZE;

    const dropdownIcon = (
        <div className="cs-dropdownWrapper">
            <Icon type="campaignSetupDropdown" />
        </div>
    );
    const filtered = TEMPLATES.filter((t) => {
        const matchSearch =
            !search ||
            t.label.toLowerCase().includes(search.toLowerCase()) ||
            t.category.toLowerCase().includes(search.toLowerCase());

        const matchCategory =
            !category || t.category === category;

        const matchDifficulty =
            !difficulty || t.difficulty === difficulty;

        return matchSearch && matchCategory && matchDifficulty;
    });

    useEffect(() => {
        setTemplatePage(1);
    }, [search, category, difficulty]);

    const paginatedTemplates = filtered.slice(
        (templatePage - 1) * pageSize,
        templatePage * pageSize
    );

    const categories = [
        { value: "", label: "Select a category" },
        { value: "AI-Driven", label: "AI-Driven" },
        { value: "Core Essentials", label: "Core Essentials" },
        { value: "Hospitality-Specific", label: "Hospitality-Specific" }
    ];

    const difficulties = [
        { value: "", label: "Select a difficulty" },
        { value: "Beginner" as string, label: "Beginner" },
        { value: "Intermediate" as string, label: "Intermediate" },
        { value: "Advanced" as string, label: "Advanced" }
    ];

    const languages = [
        { value: "", label: "Select a language" },
        { value: 1, label: "English" },
        { value: 2, label: "French" },
    ]

    return (
        <div className="campaign-template-list">
            <div>
                <div className="campaign-scenario">Scenario</div>
                <div className="campaign-scenario-subtitle">
                    Choose the scenario for the campaign
                </div>
            </div>

            <div className="d-flex gap-24 justify-between flex-wrap align-center">
                <Input
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    suffix={<Icon type="campaignSetupSearch" />}
                    className="campaign-setup-search"
                />

                <Select
                    className="ml-select campaign-setup-language-select"
                    suffixIcon={dropdownIcon}
                    value={language}
                    onChange={setLanguage}
                    options={languages}
                />

                <Select
                    className="ml-select campaign-setup-categories-select"
                    suffixIcon={dropdownIcon}
                    value={category}
                    onChange={setCategory}
                    options={categories}
                />

                <Select
                    className="ml-select campaign-setup-difficulties-select"
                    suffixIcon={dropdownIcon}
                    value={difficulty}
                    onChange={setDifficulty}
                    options={difficulties}
                />
            </div>

            <div className="cs-template-list-header">
                Select template ({filtered.length} results)
            </div>

            {filtered.length === 0 ? (
                <div className="cs-template-empty-state">
                    No templates match your filters.
                </div>
            ) : (
                <Row gutter={[20, 20]}>
                    {paginatedTemplates.map((t) => (
                        <Col key={t.value} xs={24} sm={12} md={8} lg={6}>
                            <TemplateCard
                                template={t}
                                selected={selectedTemplate === t.value}
                                onSelect={onSelect}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            {filtered.length > pageSize && (
                <div className="d-flex justify-center mt-40">
                    <Pagination
                        current={templatePage}
                        total={filtered.length}
                        pageSize={pageSize}
                        onChange={(page) => setTemplatePage(page)}
                        showSizeChanger={false}
                    />
                </div>
            )}

            <div className="d-flex justify-end mtx-8">
                <button
                    disabled={!selectedTemplate}
                    onClick={onNext}
                    style={{
                        background: selectedTemplate ? "#006CA3" : "#EAEAEB",
                        color: selectedTemplate ? "#fff" : "#999",
                        border: "none",
                        borderRadius: 8,
                        padding: "10px 32px",
                        fontWeight: 600,
                        fontSize: 15,
                        cursor: selectedTemplate ? "pointer" : "not-allowed",
                        transition: "all 0.18s",
                    }}
                >
                    Next
                </button>
            </div>
        </div>
    );
}